import React from 'react';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepLabel from '@mui/material/StepLabel';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { useHistory } from 'react-router-dom';
import GeneratedInformation from './GeneratedInformation';
import StagingSummary from './StagingSummary';
import TestExecution from './TestExecution';
import HumanValidation from './HumanValidation';
import ReleaseSummary from './ReleaseSummary';
import { DynamoDBItemExtended, DynamoDBItemWithDraft, DynamoDBItemWithRelease, DynamoDBItemWithStaging } from '../types';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import PersonIcon from '@mui/icons-material/Person';
import { TASK_STATES } from '../constants';

const STAGES = [
    'Generate Information',
    'Staging Setup',
    'Test',
    'Human Validation',
    'Production',
];

const SPACING = {
    BETWEEN_CARDS: '24px',
    WITHIN_CARD: '16px',
    BETWEEN_EQUAL: '8px',
};

const STAGE = {
    DRAFT: 0,
    STAGING: 1,
    TEST: 2,
    PRODUCTION: 3,
};

const STAGE_LABELS = ['Draft', 'Staging Setup', 'Test & Promote to Prod', 'Release'];

interface StageHeaderProps {
    index: number;
    label: string;
}

const StageHeader: React.FC<StageHeaderProps> = ({ index, label }) => {
    const circle = (
        <Box
            sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
               
                border: '3px solid #6c6c6c',
                color: '#000000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: '0.85rem',
                fontWeight: 700,
            }}
        >
            {index + 1}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {circle}
            <Typography
                variant="h3"
                sx={{
                    mx: 1.5,
                    color: '#000000',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                  
                }}
            >
                {label}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#6c6c6c' }} />
        </Box>
    );
};

const getActiveStep = (status: string): number => {
    switch (status) {
        case TASK_STATES.NEW: return 1; //coming directly from poller
        case TASK_STATES.DRAFT_CREATED: return 1; // Data created
        case TASK_STATES.DRAFT_FAILED: return 1; // Data creation failed, needs attention
        case TASK_STATES.DRAFT_APPROVED: return 1; // Approved by human, ready for staging setups creation
        case TASK_STATES.DRAFT_COMPLETED: return 1; // Generated by service, ready for staging setup
        case TASK_STATES.STAGING_RUNNING: return 2; // Staging setup started (envelopes creation in progress - staging)
        case TASK_STATES.STAGING_COMPLETED: return 2; // Staging setup done, ready for test execution
        case TASK_STATES.STAGING_FAILED: return 2; // Staging setup failed, needs attention
        case TASK_STATES.TEST_RUNNING: return 3; // Staging setup done, ready for test execution
        case TASK_STATES.TEST_FAILED: return 3; // Staging setup failed, needs attention
        case TASK_STATES.TEST_COMPLETED: return 3; // Tests passed, ready for production release
        case TASK_STATES.RELEASE_APPROVED: return 4; // Approved by human, ready for production release
        case TASK_STATES.RELEASE_RUNNING: return 4; // Release to production in progress
        case TASK_STATES.RELEASE_COMPLETED: return 4; // Released to production success
        case TASK_STATES.RELEASE_FAILED: return 4; // Released to production failed
        default: return 1;
    }
};

const getStatusColor = (status: string): any => {
    switch (status) {
        case TASK_STATES.DRAFT_CREATED: return 'informational';
        case TASK_STATES.DRAFT_APPROVED: return 'success';
        case TASK_STATES.DRAFT_FAILED: return 'error';
        default: return 'informational';
    }
};

const getStatusIcon = (status: string): any => {
    switch (status) {
        case TASK_STATES.DRAFT_APPROVED: return <fuel-icon name="check_circle" />;
        case TASK_STATES.DRAFT_CREATED: return <fuel-icon name="info" />;
        case TASK_STATES.DRAFT_FAILED: return <fuel-icon name="error" />;
        default: return <fuel-icon name="info" />;
    }
};

const timeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    const units = [
        { name: 'year' as Intl.RelativeTimeFormatUnit,   seconds: 31536000 },
        { name: 'month' as Intl.RelativeTimeFormatUnit,  seconds: 2592000 },
        { name: 'day' as Intl.RelativeTimeFormatUnit,    seconds: 86400 },
        { name: 'hour' as Intl.RelativeTimeFormatUnit,   seconds: 3600 },
        { name: 'minute' as Intl.RelativeTimeFormatUnit, seconds: 60 },
        { name: 'second' as Intl.RelativeTimeFormatUnit, seconds: 1 },
    ];

    for (const unit of units) {
        const value = Math.floor(diffInSeconds / unit.seconds);
        if (value >= 1) {
            const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
            return rtf.format(-value, unit.name);
        }
    }

    return 'just now';
};

interface StepIconProps {
    active?: boolean;
    completed?: boolean;
    icon?: React.ReactNode;
}

const StepIcon: React.FC<StepIconProps> = ({ active, completed, icon }) => {
    if (completed || active) {
        return (
            <span className={`task-stepper-icon ${completed ? 'task-stepper-icon--completed' : 'task-stepper-icon--active'}`}>
                <CheckIcon style={{ fontSize: '1rem' }} />
            </span>
        );
    }
    return (
        <span className="task-stepper-icon task-stepper-icon--pending">
            {icon}
        </span>
    );
};

interface TaskStepperProps {
    task: DynamoDBItemExtended;
}

const TaskStepper: React.FC<TaskStepperProps> = ({ task }) => {
    const history = useHistory();
    const activeStep = getActiveStep((task as DynamoDBItemWithDraft).state);
    const activeStage = getActiveStep((task as DynamoDBItemWithDraft).state);
    const [copied, setCopied] = React.useState(false);


    const handleCopyId = () => {
        navigator.clipboard.writeText(task.id || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };


    const stageState = (stage: number) => {
        return {
        completed: activeStage > stage,
            active: activeStage === stage,
            locked: activeStage < stage,
        };
    };

    return (
        <>
            <Card sx={{ p: SPACING.BETWEEN_CARDS, borderRadius: '8px'}}>
                {/* Breadcrumb */}
                <Breadcrumbs sx={{ mb: SPACING.WITHIN_CARD }}>
                    <Link
                        underline="hover"
                        color="primary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => history.push('/ediSelfServicePortal/taskQueue')}
                    >
                        Partner Manager
                    </Link>
                    <Typography color="text.primary">{task.request_id}</Typography>
                </Breadcrumbs>

                {/* Title row: ID + Badge + Save */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.BETWEEN_EQUAL }}>
                            <Typography variant="h1">{task.request_id}</Typography>
                            <Chip
                                label={(task as DynamoDBItemExtended).state}
                                color={getStatusColor((task as DynamoDBItemExtended).state)}
                                icon={getStatusIcon((task as DynamoDBItemExtended).state)}
                                variant="badge"
                                size="small"
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column'  }}>
                            <Typography variant="bodyMedium" color="text.secondary">
                                {task.supplier_name || 'Unknown Supplier Name'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="bodyMedium" color="text.secondary">
                                    ID: {task.id || 'Unknown Identifier'}
                                </Typography>
                                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                                    <IconButton
                                        size="small"
                                        onClick={handleCopyId}
                                        sx={{ p: 0.5 }}
                                    >
                                        <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            {(task as any).created_timestamp && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                                    <Typography variant="bodyMedium" color="text.secondary">
                                        {new Date((task as any).created_timestamp).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#888', mt: 0.5 }}>
                                        Created {timeAgo((task as any).created_timestamp)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: SPACING.BETWEEN_EQUAL }}>
                         <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: '#555' }}>
                            <PersonIcon sx={{ fontSize: '1rem' }} />
                            Assigned to: <strong>{task.assigned_to || 'Unassigned'}</strong>
                        </span>
                        <Typography variant="labelSmall" color="text.secondary">
                            Last update: {timeAgo((task as DynamoDBItemExtended).updated_timestamp)}
                        </Typography>
                    </Box>
                </Box>

                {/* Stepper */}
                <Stepper activeStep={activeStep} nonLinear alternativeLabel sx={{ mt: SPACING.BETWEEN_CARDS }}>
                    {STAGES.map((stage, index) => {
                        const completed = index < activeStep;
                        return (
                            <Step key={stage} completed={completed} disabled>
                                <StepButton disableRipple>
                                    <StepLabel StepIconComponent={StepIcon}>
                                        {stage}
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        );
                    })}
                </Stepper>
            </Card>

            {/* DRAFT STAGE */}
            <Box sx={{ mt: SPACING.BETWEEN_CARDS }}>
                <StageHeader index={STAGE.DRAFT} label={STAGE_LABELS[STAGE.DRAFT]} />
                <GeneratedInformation task={task as DynamoDBItemWithDraft} />
            </Box>

            {/* STAGING STAGE */}
            <Box sx={{ mt: SPACING.BETWEEN_CARDS }}>
                <StageHeader index={STAGE.STAGING} label={STAGE_LABELS[STAGE.STAGING]} />
                <StagingSummary task={(task as DynamoDBItemWithStaging)} locked={stageState(STAGE.STAGING).locked} />
            </Box>

            {/* TEST EXECUTION */}
            <Box sx={{ mt: SPACING.BETWEEN_CARDS }}>
                <StageHeader index={STAGE.TEST} label={STAGE_LABELS[STAGE.TEST]} />
                <TestExecution
                    testExecution={(task as any).staging_data?.testExecution}
                    locked={stageState(STAGE.TEST).locked}
                />
            </Box>

            {/* HUMAN VALIDATION
            <Box sx={{ mt: SPACING.BETWEEN_CARDS }}>
                <StageHeader index={STAGE.HUMAN_VALIDATION} label={STAGE_LABELS[STAGE.HUMAN_VALIDATION]} />
                <HumanValidation validation={(task as any).humanValidation} locked={false} />
            </Box> */}

            {/* PRODUCTION */}
            <Box sx={{ mt: SPACING.BETWEEN_CARDS }}>
                <StageHeader index={STAGE.PRODUCTION} label={STAGE_LABELS[STAGE.PRODUCTION]} />
                <ReleaseSummary task={(task as DynamoDBItemWithRelease)} locked={stageState(STAGE.PRODUCTION).locked} />
            </Box>
        </>
    );
};

export default TaskStepper;